import { SelectOption } from '@globaleris/ui.library'
import { EnumCurrency, EnumTipoSujeto } from '../../api'
import { apiGetConcepts } from '../../api/Argentia/endpoints/concepts/concepts.api'
import { ConceptPriceModel } from '../../api/Argentia/endpoints/concepts/model/concepts'
import {
  apiFetchProductTypeOptionsByClientType,
  apiFetchProductsBySubjectType
} from '../../api/Argentia/endpoints/contracts/contracts.api'
import { ProductSelectOption } from '../../api/Argentia/endpoints/contracts/model/contracts'
import { apiFetchLanguages } from '../../api/Argentia/endpoints/languages/languages.api'
import {
  ProductConceptPriceModel,
  ProductPriceModel,
  ProductPriceModelPatch,
  ProductPriceModelPost
} from '../../api/Argentia/endpoints/products/model/products'
import {
  apiDeleteConceptPrice,
  apiDeleteProductPriceComponent,
  apiFetchProductById,
  apiFetchProductConceptPriceById,
  apiFetchProductPriceComponents,
  apiFetchProductPriceComponentsWithDummyExpression,
  apiFetchProducts,
  apiGetWeightedTypeListByBreakdownType,
  apiPatchProductPriceComponent,
  apiPostProduct,
  apiPostProductConceptPrice,
  apiPostProductPriceComponent,
  apiUpdateProduct,
  apiUpdateProductDetail
} from '../../api/Argentia/endpoints/products/products.api'
import {
  apiFetchTemplatesByProductId,
  apiTemplateSettingsGET,
  apiTemplateSettingsUpdate
} from '../../api/Argentia/endpoints/templateSettings/templateSettings.api'
import { EnumCommodity } from '../../api/Argentia/enums'
import { EnumClientTypes } from '../../api/Argentia/enums/enumClientTypes'
import { ProductModel } from '../../api/Argentia/model/contracts'
import { enumEnumToValueNumber } from '../../helpers'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import i18n from '../../i18n'
import { ActionIcons, DataTableActionConfiguration } from '../../model'
import { ServiceResponse } from '../../model/serviceResponse'
import { getActiveCompany } from '../../redux'
import { productsToProductTableListModelMapper, templateSettingsMapper } from './products.mapper'
import { ProductDetail, ProductModelResponse, ProductsTableModel } from './products.Model'

export const fetchProducts = async ({
  clientType,
  commodity,
  includeInactives
}: {
  clientType: EnumClientTypes
  commodity: EnumCommodity
  includeInactives: boolean
}): Promise<ServiceResponse<ProductsTableModel[] | undefined>> => {
  const clientTypeNumber = enumEnumToValueNumber(EnumClientTypes, clientType)!
  const commodityNumber = enumEnumToValueNumber(EnumCommodity, commodity)!
  const activeCompany = getActiveCompany()

  return await apiFetchProducts({
    clientType: clientTypeNumber,
    commodity: commodityNumber,
    includeInactives,
    companyId: activeCompany?.id
  })
    .then(response => {
      return createSuccessServiceResponse(productsToProductTableListModelMapper(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

//#region Operation global actions

// export const filterApplicableActionsConfiguration = (
//   item: InvoicesTableModel,
//   configurations: Array<DataTableActionConfiguration>
// ): Array<DataTableActionConfiguration> => {
//   let applicableActionsConfiguration: Array<DataTableActionConfiguration> = []
//   configurations.forEach(configuration => {
//     switch (configuration.icon) {
//       case ActionIcons.ERROR: {
//         if (item.hasObjections) {
//           applicableActionsConfiguration.push(configuration)
//         }
//         break
//       }
//     }
//   })

//   return applicableActionsConfiguration
// }

export const getGlobalActionsConfiguration = (): Array<DataTableActionConfiguration> => {
  return [getObjectionsDisagreementAction()]
}

const getObjectionsDisagreementAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.ERROR,
    tooltip: i18n.t('invoicesPage:table.tooltips.objectionsError')
  }
}

export const fetchProductsBySubjectType = async (
  clientType: EnumTipoSujeto,
  commodity?: EnumCommodity
): Promise<ServiceResponse<ProductSelectOption[] | undefined>> => {
  return await apiFetchProductsBySubjectType(clientType, commodity)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchProductTypeOptionsByClientType = async (
  clientType: EnumClientTypes
): Promise<ServiceResponse<SelectOption[] | undefined>> => {
  return await apiFetchProductTypeOptionsByClientType(clientType)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createProduct = async (
  product: ProductModel
): Promise<ServiceResponse<ProductModelResponse | undefined>> => {
  return await apiPostProduct(product)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchProductById = async (
  id: number
): Promise<ServiceResponse<ProductModelResponse | undefined>> => {
  return await apiFetchProductById(id)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateProduct = async (
  product: ProductModel
): Promise<ServiceResponse<ProductModelResponse | undefined>> => {
  return await apiUpdateProduct(product)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateProductDetail = async (
  product: ProductDetail
): Promise<ServiceResponse<ProductModelResponse | undefined>> => {
  return await apiUpdateProductDetail(product)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchConcepts = async (
  currency: EnumCurrency,
  commodity: EnumCommodity | undefined,
  companyId: number,
  clientType: EnumClientTypes
): Promise<ServiceResponse<any>> => {
  return await apiGetConcepts(currency, commodity, companyId, clientType)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchLanguages = async (): Promise<ServiceResponse<any>> => {
  return await apiFetchLanguages()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchTemplates = async (): Promise<ServiceResponse<any>> => {
  return apiTemplateSettingsGET()
    .then(response => {
      return createSuccessServiceResponse(templateSettingsMapper(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const postTemplateUpdate = async (template): Promise<ServiceResponse<any>> => {
  return apiTemplateSettingsUpdate(template)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchTemplatesByProductId = async (id: number): Promise<ServiceResponse<any>> => {
  return apiFetchTemplatesByProductId(id)
    .then(response => {
      return createSuccessServiceResponse(templateSettingsMapper(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchProductPriceComponents = async (
  productId: number
): Promise<ServiceResponse<ProductPriceModel[] | undefined>> => {
  return apiFetchProductPriceComponents(productId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchProductPriceComponentsWithDummyExpression = async (
  productId: number
): Promise<ServiceResponse<ProductPriceModel[] | undefined>> => {
  return apiFetchProductPriceComponentsWithDummyExpression(productId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createPriceComponentType = async (
  productId: number,
  priceComponentId: number,
  request: ProductPriceModelPost
): Promise<ServiceResponse<ProductPriceModel | undefined>> => {
  return apiPostProductPriceComponent(productId, priceComponentId, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updatePriceComponentType = async (
  id: number,
  request: ProductPriceModelPatch
): Promise<ServiceResponse<ProductPriceModel | undefined>> => {
  return apiPatchProductPriceComponent(id, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteProductPriceComponents = async (
  id: number
): Promise<ServiceResponse<Object | undefined>> => {
  return apiDeleteProductPriceComponent(id)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchWeightedTypeListByBreakdownType = async (
  breakdownTypeId: number
): Promise<ServiceResponse<number[] | undefined>> => {
  return apiGetWeightedTypeListByBreakdownType(breakdownTypeId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getProductConceptPricesByProductID = async (
  productID: number
): Promise<ServiceResponse<ConceptPriceModel[] | undefined>> => {
  return await apiFetchProductConceptPriceById(productID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createProductConceptPrice = async (
  request: ProductConceptPriceModel
): Promise<ServiceResponse<ProductConceptPriceModel | undefined>> => {
  return await apiPostProductConceptPrice(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteConceptPrice = async (
  id: number
): Promise<ServiceResponse<Object | undefined>> => {
  return apiDeleteConceptPrice(id)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}
